import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import MarketplaceShell from './MarketplaceShell';
import { MktButton, CheckRow } from './marketplaceUi';
import { getMarketplaceState } from './marketplaceApi';

const BOOK_A_CALL_URL = import.meta.env.VITE_MARKETPLACE_BOOKING_URL || 'https://calendly.com/ad-blue-pill/bluepill-ai-demo-clone';

/**
 * Conversion screen — ported from run_another_study.html. Two option cards:
 * build a paid audience, or talk to the team (recommended).
 */
const PostStudyScreen: React.FC = () => {
  const navigate = useNavigate();
  // Price tracks the backend (twin_unit_price_cents), so this card can never show a
  // stale figure when the team changes the price.
  const [unitCents, setUnitCents] = useState<number | null>(null);
  useEffect(() => {
    getMarketplaceState().then((s) => setUnitCents(s.twin_unit_price_cents)).catch(() => {});
  }, []);
  const priceLabel = unitCents != null ? `$${(unitCents / 100).toFixed(unitCents % 100 ? 2 : 0)}` : '—';
  return (
    <MarketplaceShell maxWidth="max-w-[780px]">
      <div className="mb-5">
        <h1 className="text-[22px] font-medium text-[#111]">What would you like to do next?</h1>
        <p className="mt-1.5 text-[13px] text-[#6b6b6b]">Choose how you want to continue exploring the marketplace.</p>
      </div>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        {/* Run another study */}
        <div className="flex flex-col gap-3 rounded-xl border border-black/10 bg-white p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#E8FCFA]">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="text-[15px] font-medium text-[#111]">Run another study</div>
            <div className="mt-1 text-[13px] leading-relaxed text-[#6b6b6b]">Build your own audience — choose breakfast categories and segments and pay only for the AI Consumer Twins you recruit.</div>
          </div>
          <div className="my-1 flex items-baseline gap-1">
            <span className="text-[22px] font-medium text-[#111]">{priceLabel}</span>
            <span className="text-[13px] text-[#6b6b6b]">/ Twin</span>
          </div>
          <div className="mb-1 flex flex-col gap-2">
            <CheckRow>Any of the 4 use cases</CheckRow>
            <CheckRow>Mix and match categories and segments</CheckRow>
            <CheckRow>Scale from 20 to 1,000 Twins — you control the spend</CheckRow>
            <CheckRow>Paid via Stripe, instant access</CheckRow>
          </div>
          <div className="mt-auto pt-1">
            <MktButton fullWidth onClick={() => navigate('/marketplace/run/build')}>Build your audience →</MktButton>
          </div>
        </div>

        {/* Talk to us (recommended) */}
        <div className="flex flex-col gap-3 rounded-xl border-2 border-primary bg-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#E8FCFA]">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <span className="rounded-full bg-[#E8FCFA] px-2.5 py-1 text-[11px] font-medium text-[#0B7C74]">Recommended</span>
          </div>
          <div>
            <div className="text-[15px] font-medium text-[#111]">Talk to us about full access</div>
            <div className="mt-1 text-[13px] leading-relaxed text-[#6b6b6b]">Unlimited studies, custom segmentations. Let's find the right fit for your team.</div>
          </div>
          <div className="mb-1 flex flex-col gap-2">
            <CheckRow>Custom-built AI audiences</CheckRow>
            <CheckRow>Unlimited studies across all customer segments</CheckRow>
            <CheckRow>Tailored workflows across concepts, packaging, claims, and messaging</CheckRow>
            <CheckRow>Behavioral drivers behind every result</CheckRow>
            <CheckRow>Executive-ready outputs</CheckRow>
            <CheckRow>Dedicated BluePill team support</CheckRow>
          </div>
          <div className="mt-auto pt-1">
            <MktButton fullWidth onClick={() => { window.location.href = BOOK_A_CALL_URL; }}>Book a 15-min call →</MktButton>
          </div>
        </div>
      </div>
    </MarketplaceShell>
  );
};

export default PostStudyScreen;
